import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "[CodeWithSwiz 21] useAuth beta support for Firebase 🎉",
  "description": "It works! useAuth has beta-level support for Firebase Auth 🥳 I did not expect that to take 3 sessions.",
  "published": "2021-01-26T00:00:00.000Z",
  "categories": "CodeWithSwiz, Technical, Firebase, useAuth, Livecoding",
  "hero": "./img/screenshot-1611678995191.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`It works! `}<a parentName="p" {...{
        "href": "https://useAuth.dev"
      }}>{`useAuth`}</a>{` has beta-level support for Firebase Auth 🥳 I did not expect that to take 3 sessions.`}</p>
    <p><img parentName="p" {...{
        "src": "/6e010d587e987ec39a53120685eadc64/Working-Firebase-Auth-supporta4g430.gif",
        "alt": "Working Firebase Auth support"
      }}></img></p>
    <p>{`Are you a Firebase user? Can you `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/useAuth/pull/148"
      }}>{`please try it out`}</a>{` and tell me what's missing. It would mean a lot ❤️`}</p>
    <p><em parentName="p">{`CodeWithSwiz is a weekly live show. Like a podcast with video and fun hacking. Focused on experiments and open source. `}<a parentName="em" {...{
          "href": "https://youtube.com/swizecteller"
        }}>{`Join live Mondays`}</a></em></p>
    <lite-youtube {...{
      "videoid": "ZXDuPPker0M",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`We got to a working proof of concept in `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/codewithswiz-20-adding-firebase-support-to-useauth-pt2"
      }}>{`last week's episode`}</a>{`. Rough UI, transitions bad, state machines not synced, and we knew it worked because `}<inlineCode parentName="p">{`console.log`}</inlineCode>{` spat out a user.`}</p>
    <p>{`Encouraging but not useful.`}</p>
    <p>{`This episode was about polishing that turd into a useful beta. Improve the config experience, sync Firebase's and useAuth's state machines, recover sessions on page load, tighten up TypeScript types.`}</p>
    <p>{`And as a bonus, we ran into ye olde `}<inlineCode parentName="p">{`this`}</inlineCode>{` problem. Thought those days were behind me 🥲`}</p>
    <h2 {...{
      "id": "improve-the-config-experience"
    }}>{`Improve the config experience`}</h2>
    <p>{`You want to reduce moving parts while prototyping. Make sure you can move fast and ignore fiddly details.`}</p>
    <p>{`Hardcoding config is a time-honored tradition. A great way to leak your API keys on GitHub and `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/what-happens-when-you-push-aws-credentials-to-github"
      }}>{`get an AWS account shut down`}</a>{`, too.`}</p>
    <p>{`Here's how it works now:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// as a user

<AuthConfig
  authProvider={FirebaseUI}
  navigate={navigate}
  params={{
    // Firebase standard practice to copypasta this blob?
    firebaseConfig: {
      apiKey: "AIzaSyCdtQ6V3qDxpgDO-usa3zWvBhIJKpAd4mM",
      authDomain: "useauth-demo.firebaseapp.com",
      projectId: "useauth-demo",
      storageBucket: "useauth-demo.appspot.com",
      messagingSenderId: "520315046120",
      appId: "1:520315046120:web:4384141e88f49e638c215d",
    },
  }}
/>
`}</code></pre>
    <p>{`Reading Firebase docs, it sounds like copy-pasting your `}<inlineCode parentName="p">{`firebaseConfig`}</inlineCode>{` object is standard practice. Go into the dashboard, click a button, paste into your project.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/85be8852290ebb84602f6fba08f6d06f/e8e04/Firebase-Config-in-dashboard4aegc8.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "58.744394618834086%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'236\\'%20viewBox=\\'0%200%20400%20236\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M79%2013c0%201-1%202-2%201-2%200-3%200-3%202l-1-1c-1-3-3-2-3%201%200%204%206%204%206%201%200-2%200-2%201%200%200%201%201%202%202%201l2%201h8c2-1%203-2%204-1%200%202%202%201%202-1h1c2%203%203%202%203-1-1-4-2-5-3-3%200%202-1%202-2%201h-8l-5-1-1-1-1%201M18%2034c-4%202-5%209-1%2011%202%202%207%201%208-1%205-5-1-12-7-10m63%200c-4%202-5%209-1%2011%207%204%2014-5%207-10-3-2-3-2-6-1m287%20162l-1%206v5h5c4%200%205-1%205-4%200-6-1-8-5-8l-4%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/85be8852290ebb84602f6fba08f6d06f/ca0a1/Firebase-Config-in-dashboard4aegc8.webp 223w", "/static/85be8852290ebb84602f6fba08f6d06f/75680/Firebase-Config-in-dashboard4aegc8.webp 445w", "/static/85be8852290ebb84602f6fba08f6d06f/8d1ba/Firebase-Config-in-dashboard4aegc8.webp 890w", "/static/85be8852290ebb84602f6fba08f6d06f/3979e/Firebase-Config-in-dashboard4aegc8.webp 1270w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/85be8852290ebb84602f6fba08f6d06f/e92b6/Firebase-Config-in-dashboard4aegc8.png 223w", "/static/85be8852290ebb84602f6fba08f6d06f/e66bf/Firebase-Config-in-dashboard4aegc8.png 445w", "/static/85be8852290ebb84602f6fba08f6d06f/4ef49/Firebase-Config-in-dashboard4aegc8.png 890w", "/static/85be8852290ebb84602f6fba08f6d06f/e8e04/Firebase-Config-in-dashboard4aegc8.png 1270w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/85be8852290ebb84602f6fba08f6d06f/4ef49/Firebase-Config-in-dashboard4aegc8.png",
                "alt": "Firebase Config in dashboard",
                "title": "Firebase Config in dashboard",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Firebase Config in dashboard`}</figcaption>{`
  `}</figure></p>
    <p>{`Keeping things familiar so users don't get scared ✌️`}</p>
    <h3 {...{
      "id": "passing-an-initialized-app"
    }}>{`Passing an initialized app`}</h3>
    <p>{`You might have a Firebase app in your stack already. For authentication or otherwise.`}</p>
    <p>{`When you pass `}<inlineCode parentName="p">{`firebaseConfig`}</inlineCode>{` useAuth initializes a new app for itself. That might not be a good idea.`}</p>
    <p>{`Instead, you can pass an existing initialized firebase app.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// as a user
<AuthConfig
  authProvider={FirebaseUI}
  navigate={navigate}
  params={{
    // pass initialized app
    firebaseApp: Firebase,
  }}
/>
`}</code></pre>
    <p>{`A conditional in `}<inlineCode parentName="p">{`FirebaseUI`}</inlineCode>{` takes the params object and decides which path to take.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/providers/FirebaseUI.ts

if (params.firebaseConfig) {
  this.firebase = Firebase.initializeApp(params.firebaseConfig, "useAuth")
} else if (params.firebaseApp) {
  this.firebase = params.firebaseApp
} else {
  throw "Please provide firebaseConfig or initialized firebaseApp"
}
`}</code></pre>
    <p>{`If there's no config, we throw an error. I hope it's a helpful error ... wish I knew how to encode that in TypeScript. Feels like the type system should enforce conditionally optional params 🤔`}</p>
    <h2 {...{
      "id": "sync-firebases-and-useauths-state-machines"
    }}>{`Sync Firebase's and useAuth's state machines`}</h2>
    <p>{`You can think of Firebase and useAuth as 2 state machines running side by side. Firebase keeps track of user state, useAuth wants to keep track of user state.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/1ef0d7f73d1ac09eed5f822d4074bd4c/31d79/useAuths-state-machine8f2801.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "34.08071748878923%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'136\\'%20viewBox=\\'0%200%20400%20136\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M63%2021c-5%200-7%202-6%204%201%201%201%201%201-1%201-2%202-2%2014-3a428%20428%200%200150%201l7-1-26-1-40%201M9%2027l3%204%202%204c-1%204%202%204%2024%204%2024%200%2024%200%2024-5%200-3%202-4%204-1%202%202%2018%202%2019-1l17-1h17l-17-1-17-1c-1-3-17-3-19%200h-4c0-2-3-2-23-2s-24%200-24%202c-1%202-3%201-3-1l-1-3-2%202m181%202l-3%201-2%201h2l3%201c2%203%2018%203%2020%201l3-2-1-1-2-2c-2-2-18-1-20%201M16%2033v4h45v-9H16v5m8-2c-6%200-7%200-7%202l21%201%2021-1c0-2-1-2-9-2a158%20158%200%2000-16%200l-1-1-9%201m104%200c-3%200-4%201-4%202s2%202%2012%202l12%201h1c1-2%208-3%209-1s3%200%203-2-1-2-4-2a942%20942%200%2001-29%200m81%205c-18%200-20%201-20%204s3%204%2021%204c16%200%2018%200%2019-2%202-1%202-2%201-3v-2c1-1-4-1-21-1M66%2038l-2%202%202%202c3%203%2038%203%2041%200v-4c-3-3-37-3-41%200m84%201l-5%201c-7%200-7%200-7%202l7%201%207%201h1l2-1%203-1h1l10%201%209-1h1c1%203%204%202%204%200s0-2-19-2l-13-1h-1m3%208c0%204-2%206-4%204l-2-1h-6c-4%200-6%201-6%202%200%202%205%203%209%202h33c1%202%203%200%203-2-1-1-3-2-13-2-11%201-12%200-13-3-1-2-1-2-1%200m-88%201l-1%202%202%201a128%20128%200%200036%201l-1-3v-2l-17-1c-15%200-17%200-19%202m125-1l-3%202-2%201%202%201%203%201c3%202%2033%201%2034-1v-3c-2-3-33-3-34-1m-37%2023v5h-11l-13-1c-1-2-5-2-23-2-19%200-22%200-23%202h-4a186%20186%200%20000%2027l1-15v-1c-2-2-1-9%201-9l2%202%2023%201%2023-1c1-2%204-2%2013-2h11v3l1%203v-3c0-3%200-3%204-3h5l-6-1h-3V65l-1%205m93%202l-7%202h-4a226%20226%200%20000%2039c0-7%201-9%204-5%202%202%2038%202%2040%200l3-2%201-1h-1l-3-1c-2-3-38-3-40%200l-3%201c-2%200-2-19%200-19v-2c-2%200-1-8%200-8l3%202c2%202%2021%202%2023%200l2-2v-1l-2-1c1-2-4-3-8-3l-8%201M15%2094v22h64l-32-1H16V73h31l32-1H15v22m149%202v24h71l-35-1h-35V73h35l35-1h-71v24m203-6v18h-36l-36%201h73V73l-1%2017M171%2076c-3%200-5%202-5%203l18%201c17%200%2018-1%2018-3s-1-3-2-1h-29m-87%207c-2%202-2%202%200%204%201%202%203%202%2032%202s31%200%2033-2v-4c-2-2-4-2-33-2s-31%200-32%202m156%200l-2%202c0%203%203%204%2018%204s18-1%2018-4c0-4-2-4-18-4-12%200-15%200-16%202m83%201l-7%201c-3%200-5%201-5%202l8%201%208-1h1c1%201%206%201%206-1h1c0%202%2012%203%2013%201h1c0%202%2010%201%2010-1%200-1-1-2-7-1l-8%201h-3l2-1v-1h-8l-5%201-4%201-1%201v-1c1-2%200-3-2-2M33%2085c-1%200-2%201-1%202%200%201%208%202%208%200h18l7%201%206-1h1c1%203%204%202%204%200%200-1-1-2-3-2a124%20124%200%2000-31%201c0-1-6-2-9-1m160%208c1%203%202%203%2015%203h20l1-1v-2c1-2-26-2-29%200h-1c0-2-1-2-3-1h-1l-1-1c-1%200-2%201-1%202m120-1l-2%202c1%202%209%202%2011%201h27c16%200%2016%200%2016-2l-1-1h-37c-1-1-2-1-2%201h-1c0-2%200-2-2-1h-9M30%2096c-4%201%203%204%208%203h5l5-1h1l2%201%203-1h1c0%201%2013%202%2013%200l2%201c1%202%203%202%203-1%200-2-1-2-3-2H30m305%203h-5c-3%200-4%201-5%202h-1c0-2-12-2-13%200%200%202%201%202%205%202l6-1h1l13%201h13c1%201%206-1%206-2-1-1-3-2-8-2h-12m-157%204l-1%203h25a2810%202810%200%200030-1c0-2-1-2-8-2h-30c-1-1-2%200-2%201h-1c0-2-2-3-2-1h-11m-129%203h-8c-4%200-6%201-7%202%200%202%203%203%204%201h1l15%201c12%200%2014%200%2014-2l-8-1-9-1h-2m154%204l-6%201-5%201h-1c0-2-13-2-13%200-1%202%200%202%2018%202h19c2%202%206%200%206-2l-8-2h-10m-101%2022\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/1ef0d7f73d1ac09eed5f822d4074bd4c/ca0a1/useAuths-state-machine8f2801.webp 223w", "/static/1ef0d7f73d1ac09eed5f822d4074bd4c/75680/useAuths-state-machine8f2801.webp 445w", "/static/1ef0d7f73d1ac09eed5f822d4074bd4c/8d1ba/useAuths-state-machine8f2801.webp 890w", "/static/1ef0d7f73d1ac09eed5f822d4074bd4c/3838e/useAuths-state-machine8f2801.webp 1335w", "/static/1ef0d7f73d1ac09eed5f822d4074bd4c/e11e5/useAuths-state-machine8f2801.webp 1780w", "/static/1ef0d7f73d1ac09eed5f822d4074bd4c/11261/useAuths-state-machine8f2801.webp 1954w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/1ef0d7f73d1ac09eed5f822d4074bd4c/e92b6/useAuths-state-machine8f2801.png 223w", "/static/1ef0d7f73d1ac09eed5f822d4074bd4c/e66bf/useAuths-state-machine8f2801.png 445w", "/static/1ef0d7f73d1ac09eed5f822d4074bd4c/4ef49/useAuths-state-machine8f2801.png 890w", "/static/1ef0d7f73d1ac09eed5f822d4074bd4c/4e814/useAuths-state-machine8f2801.png 1335w", "/static/1ef0d7f73d1ac09eed5f822d4074bd4c/701e9/useAuths-state-machine8f2801.png 1780w", "/static/1ef0d7f73d1ac09eed5f822d4074bd4c/31d79/useAuths-state-machine8f2801.png 1954w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/1ef0d7f73d1ac09eed5f822d4074bd4c/4ef49/useAuths-state-machine8f2801.png",
                "alt": "useAuth's state machine",
                "title": "useAuth's state machine",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`useAuth's state machine`}</figcaption>{`
  `}</figure></p>
    <p>{`Syncing state machines is a notoriously difficult problem. You try to figure it out and after 3 days you say `}<em parentName="p">{`"Damn it, why didn't I read a book first?"`}</em></p>
    <p>{`Your best bet is the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Observer_pattern"
      }}>{`observer pattern`}</a>{`. If the target state machine supports it. Otherwise the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Actor_model"
      }}>{`actor model`}</a>{` works fine.`}</p>
    <p>{`You end up with a system like this:`}</p>
    <ol {...{
      "start": 0
    }}>
      <li parentName="ol">{`useAuth subscribes to Firebase`}</li>
      <li parentName="ol">{`User does a thing`}</li>
      <li parentName="ol">{`Firebase changes state`}</li>
      <li parentName="ol">{`Firebase tells useAuth`}</li>
      <li parentName="ol">{`useAuth changes state`}</li>
      <li parentName="ol">{`useAuth hook updates return values`}</li>
      <li parentName="ol">{`React re-renders affected components`}</li>
    </ol>
    <p>{`Two machines weakly communicating through events, independently changing their internal state. 🤘`}</p>
    <p>{`In code you get this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/providers/FirebaseUI.ts

constructor(params: AuthOptions & FirebaseOptions) {
    // ...

    // Auth state observer
    this.firebase
        .auth()
        .onAuthStateChanged(this.onAuthStateChanged.bind(this));
}

private onAuthStateChanged(user: Firebase.User | null) {
    if (user) {
        this.dispatch("LOGIN");
        this.dispatch("AUTHENTICATED", {
            user: this.firebase.auth().currentUser,
            authResult: {
                // needed for useAuth to work
                expiresIn: 3600
            }
        });
    }
}
`}</code></pre>
    <p>{`We subscribe to auth changes with a callback function. The function checks for a user, if one is present, it goes through useAuth events to log you in.`}</p>
    <p>{`We dispatch the `}<inlineCode parentName="p">{`LOGIN`}</inlineCode>{` `}<em parentName="p">{`and`}</em>{` `}<inlineCode parentName="p">{`AUTHENTICATED`}</inlineCode>{` events because `}<a parentName="p" {...{
        "href": "https://xstate.js.org/"
      }}>{`XState`}</a>{` doesn't allow jumping ahead. As it shouldn't. But we don't know when this code runs – could be on init before we start logging in.`}</p>
    <p>{`Might need to dispatch `}<inlineCode parentName="p">{`LOGOUT`}</inlineCode>{` when there's no user 🤔`}</p>
    <h2 {...{
      "id": "recover-sessions-on-page-load"
    }}>{`Recover sessions on page load`}</h2>
    <p><a parentName="p" {...{
        "href": "https://useauth.dev"
      }}>{`useAuth`}</a>{` has built-in support for validating user sessions on page load. That's like a core value prop.`}</p>
    <p>{`To make that happen, it uses a side-effect to call a `}<inlineCode parentName="p">{`checkSession`}</inlineCode>{` function on the auth provider. That method is meant to phone home, check your cookies, whatever it needs to decide `}<em parentName="p">{`"Is the current user still the current user?"`}</em></p>
    <p>{`Firebase makes that part pretty okay:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`public async checkSession(): Promise<{
    user: AuthUser;
    authResult: Auth0DecodedHash;
}> {
    // verify session is still valid
    // return fresh user info
    const user = this.firebase.auth().currentUser;

    if (user) {
        // throws if user no longer valid
        await user.reload();

        return {
            user,
            authResult: {
                // needed for useAuth to work
                expiresIn: 3600
            }
        };
    } else {
        throw new Error("Session invalid");
    }
}
`}</code></pre>
    <p>{`Grab the current user from Firebase, reload their info, return user object and fake an `}<inlineCode parentName="p">{`expiresIn`}</inlineCode>{` timer to keep useAuth happy.`}</p>
    <p>{`I considered storing the `}<inlineCode parentName="p">{`user.toJSON()`}</inlineCode>{` version of the user object, but that strips away Firebase methods. Figured people used to Firebase would want to keep those.`}</p>
    <p>{`Although the object is gnarly as heck ...`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Wow when Firebase returns the authenticated user in a callback ... I guess *some* of those are user properties 🤨 `}<a parentName="p" {...{
          "href": "https://t.co/0jtGUgjrGI"
        }}>{`pic.twitter.com/0jtGUgjrGI`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1351367255095820291"
      }}>{`January 19, 2021`}</a></blockquote>
    <h2 {...{
      "id": "the-dreaded-this"
    }}>{`The dreaded `}<inlineCode parentName="h2">{`this`}</inlineCode></h2>
    <p>{`This part.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// Auth state observer
this.firebase.auth().onAuthStateChanged(this.onAuthStateChanged.bind(this))
`}</code></pre>
    <p>{`Bloody hell I can't remember the last time I had to manually bind a function to its object. Nice reminder that `}<inlineCode parentName="p">{`class FirebaseUI {}`}</inlineCode>{` is syntax sugar for dark magic from 2010.`}</p>
    <p>{`Forget to bind and `}<inlineCode parentName="p">{`onAuthStateChanged`}</inlineCode>{` doesn't know which object it belongs to when called as a callback. Throws cryptic errors. 🙃`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/02d8a685228c66581c4b8d961c07ec04/f43e4/Fun-errors-when-you-forget-to-bindai50ih.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "12.556053811659194%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'51\\'%20viewBox=\\'0%200%20400%2051\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M299%2018v4c0%201-2%204-4%204l-2-2%202-2c2%200%201-2-1-2l1-1c1-1%201-1-1-1-3%200-3%200-3%204l1%205h32a436%20436%200%200029-3l-1-1-1%202-1%201c-1%200-3-4-2-6s0-2-1-2-2%200-1%202l-2%206c-1%200-2-1-1-4%200-2%200-2-1-1%200%204-2%204-2%200l-1-3v8l-2-1-2-1v-1c2%200%203-2%201-4-2-1-5%203-3%205%201%202%201%202-1%202-1%200-2-1-1-2l-1-2v-2c-2-2-4%201-2%203l1%201-2%201-1%201v-2c0-3-2-6-4-4v2l-1%202c1%201%200%202-1%202-2%200-2%200-1-2s1-2-1-5l-3-2v4l-1%205v-1l-2-1h-2l2-1c2-1%202-1%201-3-2-2-4%200-4%203%201%203%200%203-2%203-3%200-3-3%200-5%202-1%201-2-1-1-1%200-2%201-2%203%200%206-2%203-3-3-1-3-2-3-2-2M76%2030l-2%201c-2%201-2%201-2%203v3l-1-3c0-2%200-3-2-3s-2%201-2%203l-1%203-2-3c0-2-1-3-2-3v1c1%200%201%202-1%204a376%20376%200%200057%201l-1-2-1-1-1%202-1%201-1-3c0-3%200-3-2-3-3%200-3%200-3%203l-1%203-1-2-1-2v-2c-2%200-4%202-2%203v2h-2l-1-1h-3l2-1%202-1c-1-3-5-2-4%201%200%202-1%203-2%203l-2-2h-1v1l-1%201c-2%200-3-3-3-5l-2-1-1%201-1%203c0%202-1%202-1%201l-1-1h-2l2-1%201-1c-1-3-5-2-4%201%200%205-2%203-2-1l-1-3m63%200v1c0%203-1%206-3%206-1%200-2%200-1-1l-1-1-1%201-2%201c-1%200-2%200-1-1l-2-3%201-1c1-1%201-1-1-1l-2%202%201%201%201%201-1%201-2%201c-1%201%202%201%2017%201%2016%200%2019%200%2018-1l-1-2v-5h-4l2%201c2-1%201%201-1%204-3%204-5%203-3-1%201-5%201-5-2-5-1%200-2%200-1%201h1v4c-2%203-2%203-3%202l-1-2c2-1%201-4-1-5l-2%201v1c3-1%203%200%201%203l-1%202-1%201c-1%200-2-2-2-6%200-2-2-3-2-1M76%2041l-2%201c-2%201-2%201-2%203v3l-1-3c-1-4-4-4-4%200%200%203-2%204-3%200l-1-2v1l-1%203a384%20384%200%200057%202c0-1-3-3-4-2%200%202-2%201-2-2-1-4-5-4-5%200l-1%203v-1l-1-2-2-1h1l1-1h-4l1%203c2%201%202%201%200%201h-3c-2%200-2%200%200-2v-2c-2-2-4%200-4%203%201%202-2%203-3%201h-2c-1%202-3-1-2-3%200-2%200-2-1-1h-4l1%202c2%201-1%203-3%202-2%200-2%200%200-2v-2c-1-2-4%200-4%203v2l-1-4-1-3m68%200c-1%201%200%201%201%201v1l1%202c1%201%201%201-1%202-3%201-4%201-4-3l-1-3c-2%200-2%201-1%203%200%203-2%205-4%203h-5l-1-2v-1c2%200%201-2%200-2-3%200-3%202-2%204%202%201%202%201%200%201-7%202-2%203%2015%203l18-1-2-2%201-2v-4h-3v1c2%200%202%201%200%204l-2%203v-4c0-4-2-5-4-4l-1%204c1%202%200%203-1%203v-3c0-4-3-7-4-4\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/02d8a685228c66581c4b8d961c07ec04/ca0a1/Fun-errors-when-you-forget-to-bindai50ih.webp 223w", "/static/02d8a685228c66581c4b8d961c07ec04/75680/Fun-errors-when-you-forget-to-bindai50ih.webp 445w", "/static/02d8a685228c66581c4b8d961c07ec04/8d1ba/Fun-errors-when-you-forget-to-bindai50ih.webp 890w", "/static/02d8a685228c66581c4b8d961c07ec04/928c4/Fun-errors-when-you-forget-to-bindai50ih.webp 1120w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/02d8a685228c66581c4b8d961c07ec04/e92b6/Fun-errors-when-you-forget-to-bindai50ih.png 223w", "/static/02d8a685228c66581c4b8d961c07ec04/e66bf/Fun-errors-when-you-forget-to-bindai50ih.png 445w", "/static/02d8a685228c66581c4b8d961c07ec04/4ef49/Fun-errors-when-you-forget-to-bindai50ih.png 890w", "/static/02d8a685228c66581c4b8d961c07ec04/f43e4/Fun-errors-when-you-forget-to-bindai50ih.png 1120w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/02d8a685228c66581c4b8d961c07ec04/4ef49/Fun-errors-when-you-forget-to-bindai50ih.png",
                "alt": "Fun errors when you forget to bind",
                "title": "Fun errors when you forget to bind",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Fun errors when you forget to bind`}</figcaption>{`
  `}</figure></p>
    <h2 {...{
      "id": ""
    }}>{`🎉`}</h2>
    <p>{`And we got a working beta version.`}</p>
    <p><img parentName="p" {...{
        "src": "/6e010d587e987ec39a53120685eadc64/Working-Firebase-Auth-support40afa5.gif",
        "alt": "Working Firebase Auth support"
      }}></img></p>
    <p>{`But I don't know what's missing. Need to play around more, try different configurations and authentication modes. Figure out how to make developer experience better`}</p>
    <p>{`Are you a Firebase user? Can you `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/useAuth/pull/148"
      }}>{`please try it out`}</a>{` and tell me what's missing. It would mean a lot ❤️`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      